<template>
  <div
    v-if="pageState"
    class="list-container"
  >
    <ModalExportReservations
      v-if="showExportReservations"
      :showModal="showExportReservations"
      :onClickCancel="hideModal"
      :searchTerm="pageState.searchTerm"
      :meetingtypeId="selectedMeetingtype"
      :voucherId="voucherId"
    />

    <ui-component-drawer
      v-if="showSideDrawer"
      :drawerTitle="drawerTitle"
      :showDrawer="showSideDrawer"
      :onClickClose="hideDrawer"
      :drawerSize="drawerSize"
      position="right"
    >
      <template v-slot:content>
        <CompanySummary
          v-if="showCompanySummary"
          :companyId="itemId"
          :locationId="locationId"
        />

        <ReservationSummary
          v-if="showReservationSummary"
          :reservationKey="itemId"
          :locationId="locationId"
        />

        <CustomerSummary
          v-if="showCustomerSummary"
          :customerId="itemId"
          :locationId="locationId"
        />

        <ProposalDetail
          v-if="showProposalPreview"
          :proposalKey="selectedProposalKey"
          :isEmployee="true"
          class="proposalDetail"
        />
      </template>
    </ui-component-drawer>

    <div class="filter-column">
      <ui-search-form
        dateMode="range"
        :searchTermPlaceholder="$t('Components.Reservation.ReservationList.Placeholder_Search')"
        :isLoading="isSearching"
        :useDateInput="pageState.useDateInput"
        :startDate="pageState.startDate"
        :endDate="pageState.endDate"
        :secondStatusId="pageState.channelId"
        :secondStatusOptions="channels"
        :actionSearch="searchReservations"
      />
    </div>

    <div
      v-if="locationId > 0"
      class="has-margin-top is-hidden-print actionBar has-background-white-ter p-4"
    >
      <div class="level is-mobile">
        <div class="level-left"></div>
        <div class="level-right">
          <a
            class="has-icon has-text-link is-unselectable"
            @click="downloadAsPdf()"
          >
            <span
              class="icon"
              :class="{ 'is-loading': isCreatingPdf }"
            >
              <font-awesome-icon :icon="['fas', 'file-download']" />
            </span>
            <span>{{
              $t('Components.Reservation.ReservationList.Link_ExportPdf')
              }}</span>
          </a>

          <a
            @click="setShowExportReservations"
            class="has-icon has-text-link is-unselectable"
          >
            <span
              class="icon is-small"
              :class="{ 'is-loading': isCreatingCsv }"
            >
              <font-awesome-icon :icon="['fas', 'file-download']" />
            </span>
            <span>{{
              $t('Components.Reservation.ReservationList.Link_ExportCsv')
              }}</span>
          </a>
        </div>
      </div>
    </div>

    <!-- Desktop tabs-->
    <div
      v-if="!isMobile && pageState && pageState.statusses && pageState.statusses.length > 0 && pageState.selectedStatus"
      class="tabs is-boxed has-margin-top"
    >
      <ul>
        <li
          v-for="(status, index) in pageState.statusses"
          :key="index"
          :class="{ 'is-active' : status.status === pageState.selectedStatus.status }"
        >
          <a @click="setSelectedStatus(status.status)">{{ $t('ReservationStatus.status_' + status.status) }} ({{ status.count }})</a>
        </li>
      </ul>
    </div>

    <!-- Mobile Dropdown -->
    <div v-else class="select is-fullwidth my-4">
      <select v-model="selectedStatusId">
        <option
          v-for="(status, index) in pageState.statusses"
          :key="index"
          :value="status.status"
        >
          {{ $t('ReservationStatus.status_' + status.status) }} ({{ status.count }})
        </option>
      </select>
    </div>

    <div class="list-wrapper">
      <div class="">
        <table class="table is-fullwidth is-striped is-hoverable">
          <thead>
            <tr>
              <th width="125px">
                {{
                  $t(
                    'Components.Reservation.ReservationList.Label_ReservationId'
                  )
                }}
              </th>
              <th width="125px">
                {{ $t('Components.Reservation.ReservationList.Label_Date') }}
              </th>
              <th>
                {{ $t('Components.Reservation.ReservationList.Label_Name') }}
              </th>
              <th v-if="locationId === 0">
                {{ $t('Components.Reservation.ReservationList.Label_Channel') }}
              </th>
              <th v-if="locationId === 0">
                {{
                  $t('Components.Reservation.ReservationList.Label_Location')
                }}
              </th>
              <th v-if="showBooker">
                {{ $t('Components.Reservation.ReservationList.Label_Booker') }}
              </th>
              <th v-if="locationId > 0" width="25px">&nbsp;</th>
              <th v-if="locationId > 0">
                {{ $t('Components.Reservation.ReservationList.Label_Source') }}
              </th>
              <th>Tags</th>
              <th width="75">&nbsp;</th>
            </tr>
          </thead>
          <transition-group
            name="staggered-fade"
            v-bind:css="false"
            v-on:before-enter="beforeEnter"
            v-on:enter="enter"
            tag="tbody"
            v-if="pageState && 
            pageState.selectedStatus && 
            pageState.selectedStatus.response &&
            pageState.selectedStatus.response.Results && 
            pageState.selectedStatus.response.Results.length > 0"
          >
            <tr
              v-for="(reservation, index) in pageState.selectedStatus.response.Results"
              :key="'tr_' + index"
              :data-index="index"
            >
              <td>
                <a @click="setShowReservationSummary(reservation.Key)">#{{ reservation.Id }}</a><br />
                <span class="is-size-7">{{ $t('ReservationStatus.status_' + reservation.Status) }}</span>
              </td>
              <td nowrap>
                {{
                  reservation.Date
                    | parseIsoDateStringToDate
                    | customDateFormat('dd MMM yy', $i18n.locale)
                }}<br />
                {{ reservation.StartMinutes | minutesToTime }} -
                {{ reservation.EndMinutes | minutesToTime }}
              </td>
              <td>
                {{ reservation.Name }}
              </td>
              <td v-if="locationId === 0">{{ reservation.ChannelName }}</td>
              <td v-if="locationId === 0">{{ reservation.LocationName }}</td>
              <td v-if="showBooker">
                <div v-if="reservation.CompanyId > 0">
                  <a
                    @click="setShowCompanySummary(reservation.CompanyId)"
                    class="has-icon has-text-link "
                  >
                    <span class="icon is-small">
                      <font-awesome-icon :icon="['fas', 'building']" />
                    </span>
                    <span>{{ reservation.CompanyName }}</span>
                  </a>
                </div>

                <div v-if="reservation.CustomerId > 0">
                  <a
                    @click="setShowCustomerSummary(reservation.CustomerId)"
                    class="has-icon has-text-link "
                  >
                    <span class="icon is-small">
                      <font-awesome-icon :icon="['fas', 'user']" />
                    </span>
                    <span>{{ reservation.CustomerName }}</span>
                  </a>
                </div>
              </td>
              <td v-if="locationId > 0" width="25px" class="has-text-right">
                <span class="icon has-text-warning" v-if="reservation.InstantBooking" title="InstantBooking">
                  <font-awesome-icon :icon="['fas', 'bolt']" />
                </span>
              </td>
              <td v-if="locationId > 0">
                {{ reservation.ChannelName }} {{ reservation.LeadName }}
              </td>
              <td>
                <Tags
                  v-if="reservation.Tags.length > 0"
                  :tags="reservation.Tags.split(',')"
                />
              </td>
              <td>
                <div class="field is-grouped">
                  <p
                    v-if="!isEmployee"
                    class="control"
                  >
                    <a
                      @click="setShowReservationSummary(reservation.Key)"
                      class="is-small"
                    >
                      <span class="icon">
                        <font-awesome-icon :icon="['fas', 'share']" />
                      </span>
                    </a>
                  </p>

                  <p
                    v-if="isEmployee"
                    class="control"
                  >
                    <a
                      @click="goToReservation(reservation)"
                      class="is-small"
                      :title="$t('Components.Reservation.ReservationList.Link_EditReservation')"
                    >
                      <span class="icon">
                        <font-awesome-icon :icon="['fas', 'edit']" />
                      </span>
                    </a>
                  </p>

                  <p 
                    v-if="reservation.ProposalKey"
                    class="control"
                  >
                    <a
                      @click="setShowProposalPreview(reservation.ProposalKey)"
                      class="is-small"
                      :title="$t('Components.Reservation.ReservationList.Link_PreviewProposal')"
                    >
                      <span class="icon">
                        <font-awesome-icon :icon="['fas', 'file-invoice']" />
                      </span>
                    </a>
                  </p>
                </div>
              </td>
            </tr>
          </transition-group>
        </table>

        <transition
          name="fade"
          mode="out-in"
        >
          <Message
            v-if="!isSearching &&
            pageState &&
            pageState.selectedStatus &&
            pageState.selectedStatus.response &&
            pageState.selectedStatus.response.Results && 
            pageState.selectedStatus.response.Results.length === 0"
            :message="$t('Components.Reservation.ReservationList.Message_NoReservations')"
          />
        </transition>

        <transition
          name="fade"
          mode="out-in"
        >
          <ui-loader v-if="isSearching" />
        </transition>

        <div v-if="!isSearching">
          <Pagination
            v-if="pageState.selectedStatus &&
            pageState.selectedStatus.response &&
            (pageState.selectedStatus.response.MorePages > 0 ||
              pageState.selectedStatus.page > 1)"
            :currentPage="pageState.selectedStatus.page ? pageState.selectedStatus.page : 1"
            :morePages="pageState.selectedStatus.response.MorePages"
            @pageSelected="pageSelected"
          />
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex'
import staggeredAnimation from '@/mixins/staggeredAnimation'
import reservationProvider from '@/providers/reservation'
import dataUriToBlob from '@/mixins/dataUriToBlob'
import { format } from 'date-fns'

import Tags from '../UI/Tags.vue'

export default {
  components: {
    CompanySummary: () => import(/* webpackChunkName: "reservation-list" */ '@/components/Companies/CompanySummary'),
    CustomerSummary: () => import(/* webpackChunkName: "reservation-list" */ '@/components/Customers/CustomerSummary'),
    Message: () => import(/* webpackChunkName: "reservation-list" */ '@/components/UI/Message'),
    Pagination: () => import(/* webpackChunkName: "reservation-list" */ '@/components/UI/Pagination'),
    ReservationSummary: () => import(/* webpackChunkName: "reservation-list" */ '@/components/Reservations/ReservationSummary'),
    ModalExportReservations: () => import(/* webpackChunkName: "reservation-list" */ '@/components/Reservations/ModalExportReservations'),
    ProposalDetail: () => import(/* webpackChunkName: "reservation-detail" */ '@/components/Proposals/BookerPreview/ProposalDetail.vue'),
    Tags
  },

  props: {
    locationId: {
      default: 0,
      type: Number,
    },

    profileId: {
      default: 0,
      type: Number,
    },

    customerId: {
      default: 0,
      type: Number,
    },

    companyId: {
      default: 0,
      type: Number,
    },

    voucherId: {
      default: 0,
      type: Number,
    },

    selectedMeetingtype: {
      type: Number,
      default: 0,
    },

    showBooker: {
      type: Boolean,
      default: true,
    },

    showCompany: {
      type: Boolean,
      default: true,
    },

    breakpoint: {
      type: Number,
      default: 768 // Bulma tablet breakpoint
    }
  },

  mixins: [staggeredAnimation, dataUriToBlob],

  data() {
    return {
      isCreatingCsv: false,
      isCreatingPdf: false,
      itemId: 0,
      isMobile: false,
      pageState: this.$store.getters['reservationStore/getReservationsPageState'],
      drawerSize: '',
      selectedProposalKey: '',
      showCompanySummary: false,
      showCustomerSummary: false,
      showExportReservations: false,
      showSideDrawer: false,
      showReservationSummary: false,
      showProposalPreview: false,
      isSearchingCounter: 0,
      selectedStatusId: 0
    }
  },

  watch: {
    selectedStatusId(val) {
      this.setSelectedStatus(val);
    },
    selectedMeetingtype: {
      handler: function(newVal) {
        this.setMeetingtype(newVal);
      },
    },
  },

  computed: {
    ...mapState('locationStore', ['locationState']),

    isSearching() {
      return this.isSearchingCounter > 0
    },

    channels() {
      let channels = []

      if (this.locationState &&
        this.locationState.LocationAccountRights &&
        this.locationState.LocationAccountRights.AllowedChannels.length > 0
      ) {
        channels.push({
          id: 0,
          text: this.$t('Components.Reservation.ReservationList.Label_AllChannels')
        })

        for (let i = 0; i < this.locationState.LocationAccountRights.AllowedChannels.length; i++) {
          let channel = this.locationState.LocationAccountRights.AllowedChannels[i]
          channels.push({
            id: channel.Id,
            text: channel.Name
          })
        }
      }

      return channels
    },

    isEmployee() {
      let employee = false

      if (this.locationState && this.locationState.LocationAccountRights) {
        employee = true
      }

      return employee
    },

    mustConfirmReservations() {
      let result = false

      if (this.locationState) {
        result =
          this.locationState.Meetingtypes.findIndex(
            (m) => m.MustBeConfirmed
          ) > -1
      }

      return result
    }
  },

  created() {
    if (this.companyId > 0 || this.profileId > 0) {
      let currentDate = new Date()

      this.pageState.startDate = new Date(
        currentDate.getFullYear(),
        0,
        1,
        currentDate.getHours(),
        currentDate.getMinutes()
      )
      this.pageState.endDate = new Date(
        currentDate.getFullYear(),
        11,
        31,
        currentDate.getHours(),
        currentDate.getMinutes()
      )
    }

    if (this.selectedMeetingtype) {
      this.pageState.meetingtypeId = this.selectedMeetingtype
    }

    this.getReservationStatusCount();
  },

  mounted() {
    this.checkViewport();
    window.addEventListener('resize', this.checkViewport);
  },

  beforeDestroy() {
    if (this.companyId > 0 || this.profileId > 0) {
      this.resetReservationPageState()
    }

    reservationProvider.methods.cancelGetLocationReservationsRequest();

    window.removeEventListener('resize', this.checkViewport);
  },

  methods: {
    ...mapActions('reservationStore', ['resetReservationPageState']),
    ...mapMutations('reservationStore', [
      'setReservation',
      'setReservationsPageState',
    ]),

    checkViewport() {
      this.isMobile = window.innerWidth < this.breakpoint
    },

    getReservationStatusCount() {
      if (this.profileId > 0 && this.locationId === 0) {
        this.getProfileReservationStatusCount();
      }
      if (this.locationId > 0) {
        this.getLocationReservationStatusCount();
      }
    },

    getReservations() {
      if (this.profileId > 0 && this.locationId === 0) {
        this.getProfileReservations();
      }
      if (this.locationId > 0) {
        this.getLocationReservations();
      }
    },

    getProfileReservationStatusCount() {
      this.isSearchingCounter++
      reservationProvider.methods.getProfileReservationStatusCount(
        this.pageState.channelId,
        this.locationId,
        this.pageState.searchTerm,
        format(this.pageState.startDate, 'yyyy-MM-dd'),
        format(this.pageState.endDate, 'yyyy-MM-dd'),
        this.pageState.meetingtypeId,
        this.companyId,
        this.customerId,
        this.leadId,
        this.voucherId
      )
        .then((response) => {
          if (response.status === 200) {
            let data = response.data
            let statusses = [];

            for (let i = 0; i < data.length; i++) {
              statusses.push({ status: data[i].Status, count: data[i].NrOfReservations, response: null, page: 1 });
            }

            this.pageState.statusses = statusses;
            this.setReservationsPageState(this.pageState);
          }
        })
        .finally(() => {
          this.isSearchingCounter = Math.max(--this.isSearchingCounter, 0)
          if (this.pageState.selectedStatus === null) {
            this.selectedStatusId = this.pageState.statusses[2].status;
            this.setSelectedStatus(this.pageState.statusses[2].status);
          } else {
            this.getReservations();
          }
        })
    },

    getLocationReservationStatusCount() {
      this.isSearchingCounter++

      reservationProvider.methods.getLocationReservationStatusCount(
        this.pageState.channelId,
        this.locationId,
        this.pageState.searchTerm,
        format(this.pageState.startDate, 'yyyy-MM-dd'),
        format(this.pageState.endDate, 'yyyy-MM-dd'),
        this.pageState.meetingtypeId,
        this.companyId,
        this.customerId,
        this.leadId,
        this.voucherId
      )
        .then((response) => {
          if (response.status === 200) {
            let data = response.data
            let statusses = [];

            for (let i = 0; i < data.length; i++) {
              statusses.push({ status: data[i].Status, count: data[i].NrOfReservations, response: [], page: 1 });
            }

            this.pageState.statusses = statusses;
            this.setReservationsPageState(this.pageState);
          }
        })
        .finally(() => {
          this.isSearchingCounter = Math.max(--this.isSearchingCounter, 0)
          if (this.pageState.selectedStatus === null) {
            this.selectedStatusId = this.pageState.statusses[2].status;
            this.setSelectedStatus(this.pageState.statusses[2].status);
          } else {
            this.getReservations();
          }
        })
    },

    /**
     *  Get location reservations
     */
    async getLocationReservations() {
      this.isSearchingCounter++

      // Clear store data 
      reservationProvider.methods.cancelGetLocationReservationsRequest()

      reservationProvider.methods.getLocationReservations(
        this.pageState.searchTerm,
        this.pageState.channelId,
        this.locationId,
        this.customerId,
        this.companyId,
        this.pageState.meetingtypeId,
        this.pageState.selectedStatus.status,
        format(this.pageState.startDate, 'yyyy-MM-dd'),
        format(this.pageState.endDate, 'yyyy-MM-dd'),
        this.pageState.selectedStatus.page,
        this.pageState.itemsPerPage,
        this.voucherId
      )
        .then((response) => {
          if (response.status === 200) {
            this.pageState.selectedStatus.response = response.data;
            this.setReservationsPageState(this.pageState);
          }
        })
        .catch(() => {
        })
        .finally(() => {
          this.isSearchingCounter = Math.max(--this.isSearchingCounter, 0)
        });
    },

    /**
     * Get profile reservations
     */
    getProfileReservations() {
      this.isSearchingCounter++

      // Prepare start date
      let startDate = this.pageState.useDateInput
        ? this.pageState.startDate
        : new Date(1900, 0, 1)
      startDate = format(startDate, 'yyyy-MM-dd')

      // Prepare end date
      let endDate = this.pageState.useDateInput
        ? this.pageState.endDate
        : new Date(1900, 0, 1)
      endDate = format(endDate, 'yyyy-MM-dd')

      reservationProvider.methods.getBookerReservations(
        this.pageState.meetingtypeId,
        this.pageState.selectedStatus.status,
        this.locationId,
        this.pageState.selectedStatus.page,
        this.pageState.itemsPerPage,
        this.pageState.searchTerm,
        startDate,
        endDate)
        .then((response) => {
          if (response.status === 200) {
            this.pageState.selectedStatus.response = response.data;
            this.setReservationsPageState(this.pageState);
          }
        })
        .catch(() => {
        })
        .finally(() => {
          this.isSearchingCounter = Math.max(--this.isSearchingCounter, 0)
        });
    },

    setMeetingtype(meetingtypeId) {
      this.pageState.meetingtypeId = meetingtypeId;
      this.setReservationsPageState(this.pageState);

      this.getReservationStatusCount();
    },

    setSelectedStatus(statusId) {
      let statusIndex = this.pageState.statusses.findIndex((s) => s.status === statusId);
      if (statusIndex > -1) {
        this.pageState.selectedStatus = this.pageState.statusses[statusIndex];
        this.setReservationsPageState(this.pageState);
        // get reservations
        this.getReservations();
      }
    },

    searchReservations(searchQuery) {
      this.pageState.selectedStatus.page = 1;
      this.pageState.selectedStatus.response.Results = [];

      let now = new Date();
      let halfYearPast = new Date(now);
      halfYearPast.setMonth(halfYearPast.getMonth() - 6);

      let datePast = new Date(now);
      datePast.setFullYear(datePast.getFullYear() - 1);

      let dateFuture = new Date(now);
      dateFuture.setFullYear(dateFuture.getFullYear() + 2);

      this.pageState.channelId = searchQuery.secondStatusId;
      this.pageState.startDate = searchQuery.startDate;
      this.pageState.endDate = searchQuery.endDate;
      this.pageState.searchTerm = searchQuery.searchTerm;
      this.setReservationsPageState(this.pageState);

      // Start searching
      this.getReservationStatusCount();
    },

    async downloadAsPdf() {
      let self = this
      if (!self.isCreatingPdf) {
        self.isCreatingPdf = true

        let startDate = self.pageState.useDateInput
          ? self.pageState.startDate
          : new Date(1900, 0, 1)
        startDate = self.$options.filters.dateObjectIsoDateString(startDate)

        let endDate = self.pageState.useDateInput
          ? self.pageState.endDate
          : new Date(1900, 0, 1)
        endDate = self.$options.filters.dateObjectIsoDateString(endDate)

        let response = await reservationProvider.methods.exportReservationsAsPdf(
          self.pageState.searchTerm,
          self.pageState.channelId,
          self.locationId,
          self.profileId,
          self.companyId,
          self.pageState.meetingtypeId,
          self.pageState.selectedStatus.status,
          startDate,
          endDate
        )
          .catch(() => {
            self.isCreatingPdf = false
          })
          .finally(() => {
            self.isCreatingPdf = false
          })

        const linkSource = `data:application/pdf;base64,${response.data}`
        const pdfBlob = this.dataURItoBlob(linkSource)
        const blobUrl = window.URL.createObjectURL(pdfBlob)

        /**
         * Internet Explorer stuff!
         */
        if (window.navigator && window.navigator.msSaveOrOpenBlob) {
          window.navigator.msSaveOrOpenBlob(pdfBlob, `${Date.now()}.pdf`)
          return
        }

        const link = document.createElement('a')
        link.href = blobUrl
        link.setAttribute('download', `${Date.now()}.pdf`)
        document.body.appendChild(link)
        link.click()
        window.URL.revokeObjectURL(blobUrl)

        link.remove()

        self.isCreatingPdf = false
        return response
      }
    },

    pageSelected(page) {
      this.pageState.selectedStatus.page = page;
      this.setReservationsPageState(this.pageState);
      this.getReservations();
    },

    setShowCompanySummary(companyId) {
      this.itemId = companyId
      this.drawerTitle = this.$t('Components.Reservation.ReservationList.DrawerTitle_CompanyDetails')
      this.drawerSize = 'large'
      this.showSideDrawer = true
      this.setVisibleSideBarItem('companySummary')
    },

    setShowCustomerSummary(customerId) {
      this.itemId = customerId
      this.drawerTitle = this.$t('Components.Reservation.ReservationList.DrawerTitle_CustomerDetails')
      this.drawerSize = ''
      this.showSideDrawer = true
      this.setVisibleSideBarItem('customerSummary')
    },

    setShowReservationSummary(reservationKey) {
      this.itemId = reservationKey
      this.drawerTitle = this.$t('Components.Reservation.ReservationList.DrawerTitle_ReservationDetails')
      this.drawerSize = 'extra-large'
      this.showSideDrawer = true
      this.setVisibleSideBarItem('reservationSummary')
    },

    setVisibleSideBarItem(item) {
      this.showCompanySummary = item === 'companySummary'
      this.showReservationSummary = item === 'reservationSummary'
      this.showCustomerSummary = item === 'customerSummary'
    },

    setShowProposalPreview(proposalKey) {
        this.showSideDrawer = true;
        this.showProposalPreview = true;
        this.selectedProposalKey = proposalKey;
        this.drawerSize = 'extra-large';
    },

    hideDrawer() {
      this.itemId = 0
      this.selectedProposalKey = ''
      this.showProposalPreview = false;
      this.showSideDrawer = false
        ; (this.itemId = 0), this.setVisibleSideBarItem('')
    },

    goToReservation(reservation) {
      this.setReservation(reservation)

      this.$router.push({
        name: `reservations-list-detail`,
        params: { reservationKey: reservation.Key },
      })
    },

    setShowExportReservations() {
      this.showExportReservations = true
      this.isCreatingCsv = true
    },

    hideModal() {
      this.showExportReservations = false
      this.isCreatingCsv = false
    },
  },
}
</script>

<style lang="sass" scoped>
.proposalDetail
  :deep(.section)
    padding-inline: 0 !important

.title-date
  &:first-letter
    text-transform: uppercase
</style>